import "./EngagementManager.scss";
import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { ToastMessage } from "../../../components/CommonUIComponents/ToastMessage/ToastMessage";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MotifCheckbox,
  MotifDatePicker,
  MotifFormField,
  MotifTooltip,
} from "@ey-xd/motif-react";
import { useI18n } from "../../../hooks/useI18n";
import { Table } from "../../../components/CommonUIComponents/Table/Table";
import { SearchInput } from "../../../components/CommonUIComponents/SearchInput/SearchInput";
import { Select } from "../../../components/CommonUIComponents/Select/Select";
import {
  PipelineCard,
  PipelineCardGroup,
} from "../../../components/CommonUIComponents/PipelineCard/PipelineCard";
import {
  DownloadIcon,
  ThumbsUpIcon,
  DelegatedIcon,
  HelpIcon,
} from "../../../icons";
import { handleFilterChange } from "../../../util/helpers";
import { toArrayOrNull, toDateString } from "../../../util/filterHelpers";
import { exportMyOrders } from "../../../store/services/engagementManager.service";
import {
  statusClassMap,
  pipelineStageConfig,
  getColumnDefs,
  SEARCH_DEBOUNCE_MS,
} from "./EngagementManager.data";
import { routes } from "../../../routes/routes.constatns";
import {
  fetchOrders,
  fetchEMFilters,
  fetchStatusCounts,
  acknowledgeOrderRequest,
} from "../../../store/slices/engagementManagerSlice";

const buildStatusBadge = (value) => {
  if (!value) return <span>--</span>;
  const lower = value.toLowerCase();
  let modifier = statusClassMap[value];
  if (!modifier) {
    if (lower.includes("cancelled")) modifier = "em-status-badge--cancelled";
    else if (lower.includes("completed"))
      modifier = "em-status-badge--completed";
    else modifier = "em-status-badge--amber";
  }
  return <span className={`em-status-badge ${modifier}`}>{value}</span>;
};

function EngagementManager() {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { domain } = useParams();

  const {
    rows,
    loading,
    filterOptions,
    statusCounts,
    acknowledgeLoading,
  } = useSelector((state) => state.engagementManager);

  const currentUser = useSelector((state) => state.user.user);
  const userEmail = currentUser?.username || currentUser?.email || "";

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    if (location.state?.toast) {
      setToastMessage(location.state.toast);
      setToastVariant("success");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.toast]);

  const [searchTerm, setSearchTerm] = useState("");
  const [clientFilter, setClientFilter] = useState([]);
  const [serviceFilter, setServiceFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [emFilter, setEmFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState([null, null]);
  const [activeStatusButton, setActiveStatusButton] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isCurrentUserDelegate, setIsCurrentUserDelegate] = useState(false);

  const tableKeyRef = useRef(crypto.randomUUID());
  const searchTimerRef = useRef(null);
  const debouncedSearchRef = useRef("");

  useEffect(() => {
    if (!userEmail) return;
    dispatch(
      fetchEMFilters({
        domain,
        engagementManagerMailId: userEmail,
        superRegion: null,
      }),
    );
    dispatch(
      fetchStatusCounts({
        domain,
        engagementManagerMailId: userEmail,
        superRegion: null,
      }),
    );
  }, [dispatch, domain, userEmail]);

  const dispatchFetchOrders = useCallback(
    (overrides = {}) => {
      dispatch(
        fetchOrders({
          domain,
          pageNumber: 1,
          search: debouncedSearchRef.current || null,
          clientName: clientFilter,
          serviceName: serviceFilter,
          status: statusFilter,
          engagementManager: emFilter,
          engagementManagerMailId: userEmail,
          date: dateFilter,
          statusButtons: activeStatusButton ? [activeStatusButton] : null,
          superRegion: null,
          isCurrentUserDelegate,
          ...overrides,
        }),
      );
    },
    [
      dispatch,
      domain,
      clientFilter,
      serviceFilter,
      statusFilter,
      emFilter,
      userEmail,
      dateFilter,
      activeStatusButton,
      isCurrentUserDelegate,
    ],
  );

  useEffect(() => {
    dispatchFetchOrders();
  }, [dispatchFetchOrders]);

  const handleSearchChange = (event) => {
    const value = event?.target ? event.target.value : event;
    setSearchTerm(value || "");

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!value) {
      debouncedSearchRef.current = "";
      dispatchFetchOrders({ search: null });
      return;
    }

    searchTimerRef.current = setTimeout(() => {
      debouncedSearchRef.current = value;
      dispatchFetchOrders({ search: value });
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchClear = () => {
    setSearchTerm("");
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    debouncedSearchRef.current = "";
    dispatchFetchOrders({ search: null });
  };

  const handleCardClick = (statusButton) => {
    setActiveStatusButton((prev) =>
      prev === statusButton ? null : statusButton,
    );
  };

  const handleAcknowledge = useCallback(
    async (requestID) => {
      if (acknowledgeLoading) return;
      const result = await dispatch(
        acknowledgeOrderRequest({
          requestIds: [requestID],
          engagementManagerEmail: userEmail,
          domain,
        }),
      );
      if (!result.error) {
        setToastMessage("Order acknowledged successfully!");
        setToastVariant("success");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        dispatchFetchOrders();
        dispatch(
          fetchStatusCounts({ domain, engagementManagerMailId: userEmail }),
        );
      } else {
        setToastMessage("Failed to acknowledge order");
        setToastVariant("error");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      }
    },
    [acknowledgeLoading, dispatch, dispatchFetchOrders, userEmail, domain],
  );

  const handleSelectionChanged = useCallback((event) => {
    setSelectedRows(event.api.getSelectedRows());
  }, []);

  const handleRowClick = useCallback(
    (event) => {
      if (!domain || !event?.data) return;

      const isSelectionColumn = Boolean(
        event?.colDef?.checkboxSelection ||
        event?.colDef?.headerCheckboxSelection,
      );
      if (isSelectionColumn) return;

      const field = event?.colDef?.field;
      if (field === "acknowledged" || field === "isDelegated") return;

      navigate(`/${domain}/${routes.viewDetails}`, {
        state: { engagementData: event.data, from: routes.engagementManager },
      });
    },
    [domain, navigate],
  );

  const handleBulkAcknowledge = async () => {
    const unacknowledged = selectedRows.filter((r) => !r.acknowledged);
    if (unacknowledged.length === 0) return;

    const requestIds = unacknowledged.map((r) => r.requestID);
    const result = await dispatch(
      acknowledgeOrderRequest({
        requestIds,
        engagementManagerEmail: userEmail,
        domain,
      }),
    );

    setSelectedRows([]);
    if (!result.error) {
      setToastMessage("Selected orders acknowledged successfully!");
      setToastVariant("success");
    } else {
      setToastMessage("Failed to acknowledge selected orders");
      setToastVariant("error");
    }
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
    dispatchFetchOrders();
    dispatch(fetchStatusCounts({ domain, engagementManagerMailId: userEmail }));
  };

  const handleExportExcel = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      const dto = {
        search: debouncedSearchRef.current || null,
        clientName: toArrayOrNull(clientFilter),
        serviceName: toArrayOrNull(serviceFilter),
        status: toArrayOrNull(statusFilter),
        statusButtons: activeStatusButton ? [activeStatusButton] : null,
        engagementManager: toArrayOrNull(emFilter),
        engagementManagerMailId: userEmail || null,
        startDate: toDateString(dateFilter?.[0]) || null,
        endDate: toDateString(dateFilter?.[1]) || null,
        superRegion: null,
        isCurrentUserDelegate: isCurrentUserDelegate || null,
      };
      await exportMyOrders(dto, domain);
    } catch {
      setToastMessage("Failed to download Excel");
      setToastVariant("error");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setExportLoading(false);
    }
  };

  const columnDefs = useMemo(
    () =>
      getColumnDefs(
        t,
        buildStatusBadge,
        ThumbsUpIcon,
        handleAcknowledge,
        DelegatedIcon,
      ),
    [t, handleAcknowledge],
  );

  return (
    <div className="engagement-manager">
      <div className="em-header">
        <h1 className="em-header-title">{t("em_pageTitle")}</h1>
        <button
          className={`em-outline-btn${exportLoading ? " em-outline-btn--disabled" : ""}`}
          type="button"
          disabled={exportLoading}
          onClick={handleExportExcel}
        >
          <span className="em-outline-btn-icon">
            <DownloadIcon />
          </span>
          {t("rmq_downloadExcel")}
        </button>
      </div>

      <PipelineCardGroup>
        {pipelineStageConfig.map((stage, i) => (
          <PipelineCard
            key={i}
            count={statusCounts[stage.countKey] ?? 0}
            label={t(stage.labelKey)}
            className={`${stage.gradientClass}${activeStatusButton === stage.statusButton ? " pipeline-card--active" : ""}`}
            Icon={stage.Icon}
            onClick={() => handleCardClick(stage.statusButton)}
          />
        ))}
      </PipelineCardGroup>

      <div className="em-filters-card">
        <div className="em-filters-grid">
          <div className="em-filter-field">
            <label className="em-filter-label">{t("rmq_clientName")}</label>
            <Select
              placeholder={t("rmq_all")}
              options={filterOptions.clientNames}
              value={clientFilter}
              onChange={handleFilterChange(setClientFilter)}
              multiple
            />
          </div>
          <div className="em-filter-field">
            <label className="em-filter-label">{t("rmq_serviceName")}</label>
            <Select
              placeholder={t("rmq_all")}
              options={filterOptions.serviceNames}
              value={serviceFilter}
              onChange={handleFilterChange(setServiceFilter)}
              multiple
            />
          </div>
          <div className="em-filter-field">
            <label className="em-filter-label">{t("rmq_status")}</label>
            <Select
              placeholder={t("rmq_all")}
              options={filterOptions.statuses}
              value={statusFilter}
              onChange={handleFilterChange(setStatusFilter)}
              multiple
            />
          </div>
          {/* commented the Engagement Manager filter  */}
          {/* <div className="em-filter-field">
            <label className="em-filter-label">
              {t("em_engagementManager")}
            </label>
            <Select
              placeholder={t("rmq_all")}
              options={filterOptions.engagementManagers}
              value={emFilter}
              onChange={handleFilterChange(setEmFilter)}
              multiple
            />
          </div> */}
          <div className="em-filter-field">
            <div className="em-filter-label-row">
              <label className="em-filter-label">{t("rmq_dateRange")}</label>
              <MotifTooltip
                trigger={
                  <span
                    className="em-filter-label-info"
                    aria-label={t("rmq_dateRange_tooltip", {
                      defaultValue:
                        "Data is being filtered based on Expected Completion Date",
                    })}
                    tabIndex={0}
                  >
                    <HelpIcon />
                  </span>
                }
                allowClick={false}
                allowHover={true}
                flip={true}
                hideCloseButton={true}
              >
                {t("rmq_dateRange_tooltip", {
                  defaultValue:
                    "Data is being filtered based on Expected Completion Date",
                })}
              </MotifTooltip>
            </div>
            <MotifFormField className="em-datepicker-form-field">
              <MotifDatePicker
                value={dateFilter}
                onChange={(val) => setDateFilter(val || [null, null])}
                format="dd/MM/yyyy"
                range
                clearIcon="✕"
              />
            </MotifFormField>
          </div>
        </div>
        <div className="em-filters-actions">
          <label className="em-delegated-checkbox">
            <MotifCheckbox
              checked={isCurrentUserDelegate}
              onChange={() => setIsCurrentUserDelegate((prev) => !prev)}
            />
            <span>
              {t("em_delegatedRecords", { defaultValue: "Delegated Records" })}
            </span>
          </label>
          <div className="em-filter-field-search-btn">
            <SearchInput
              placeholder={t("search")}
              value={searchTerm}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
            />
          </div>
          <button
            className={`em-outline-btn${selectedRows.length === 0 ? " em-outline-btn--disabled" : ""}`}
            type="button"
            disabled={selectedRows.length === 0}
            onClick={handleBulkAcknowledge}
          >
            <span className="em-outline-btn-icon">
              <DownloadIcon />
            </span>
            {t("em_acknowledgeSelected")}
          </button>
        </div>
      </div>

      <div className="em-table-wrap">
        <Table
          rowData={rows}
          colDefs={columnDefs}
          pagesize={10}
          rowSelect="multiple"
          rowHeight={44}
          headerHeight={44}
          selectionChanged={handleSelectionChanged}
          forceUpdate={tableKeyRef.current}
          loading={loading}
          rowClicked={handleRowClick}
          noRowsMessage="No record found"
        />
      </div>

      {showToast && (
        <ToastMessage
          variant={toastVariant}
          message={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
}

export default EngagementManager;
